import { t } from "@travily/i18n";
import type { ProgramAvailabilityMode, ProgramsSession } from "@travily/types/models";
import { ScrollArea } from "@ui/components/ScrollArea";
import { tv } from "@ui/lib/tv";
import { AvailabilityItem } from "./AvailabilityItem";
import { FlexibleDatesPicker } from "./FlexibleDatesPicker";
import { formatSessionRange } from "./format";

// Figma "Card / When": white card with the ActionPill shadow and a bordered
// header, holding either the dated session list or the flexible date picker.
// Every session links to a pre-drafted enquiry conversation — real booking
// (and per-session availability states) comes later.
//
// The card caps at the viewport remainder so the "Got questions" pill below
// never drops under the fold: on short viewports only the session list
// scrolls; on tall ones the cap never bites and nothing scrolls.
const availabilityCard = tv({
  slots: {
    root: [
      "flex max-h-[calc(100dvh-8rem)] flex-col rounded-3xl bg-white p-5",
      "shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05),0px_3px_3px_0px_rgba(0,0,0,0.1),inset_0px_0px_0px_1px_rgba(0,0,0,0.05)]",
    ],
    header: "border-neutral-100 border-b pb-4 font-extrabold text-2xl text-neutral-900 leading-8 tracking-[-0.24px]",
    list: "mt-5 min-h-0",
    listViewport: "flex flex-col gap-3",
  },
});

export interface AvailabilityCardProps {
  mode: ProgramAvailabilityMode;
  /** Upcoming sessions, chronological (empty for anytime programs). */
  sessions: ProgramsSession[];
  /** `${messagesPath()}?to=<handle>` — enquiry links append `&draft=`. */
  messageHref: string;
  className?: string;
}

export function AvailabilityCard({ mode, sessions, messageHref, className }: AvailabilityCardProps) {
  // A published sessions-mode program can legitimately have only past
  // sessions; with nothing offerable the card carries no signal.
  if (mode === "sessions" && sessions.length === 0) return null;

  const styles = availabilityCard();

  return (
    <section className={styles.root({ className })}>
      <h2 className={styles.header()}>{t("components.views.programs.show.availability_title")}</h2>
      {mode === "sessions" ? (
        <ScrollArea className={styles.list()} inset="none" render={<ul />} viewportClassName={styles.listViewport()}>
          {sessions.map((session) => {
            const dates = formatSessionRange(session.start_date, session.end_date);
            const draft = session.name
              ? t("components.views.programs.show.availability_draft_session", { name: session.name, dates })
              : t("components.views.programs.show.availability_draft_session_dates", { dates });
            return (
              <li key={session.id}>
                <AvailabilityItem
                  state="neutral"
                  title={session.name ?? dates}
                  body={session.name ? dates : undefined}
                  href={`${messageHref}&draft=${encodeURIComponent(draft)}`}
                />
              </li>
            );
          })}
        </ScrollArea>
      ) : (
        <FlexibleDatesPicker className="mt-5" messageHref={messageHref} />
      )}
    </section>
  );
}
